import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { Store, StoreView } from '@interfaces/store/1.0';
import { StoreTaxOverrides } from '@interfaces/tax/1.0';
import { StoreConfigStatusService } from '@services/store/1.0/store-config-status.service';
import { StoreService } from '@services/store/1.0/store.service';
import { TaxService } from '@services/tax/1.0/tax.service';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { OutOfStockUpdateInput } from '@generated/tcea/models/out-of-stock-update-input';
import { IsNumberService } from '@services/utilities/1.0/is-number.service';
@Injectable({
  providedIn: 'root',
})
export class StoreResolverService implements Resolve<Store> {
  // separate error Subject because errors close the Observable
  storeSubject = new BehaviorSubject<Store>(undefined);
  errorSubject = new BehaviorSubject<any>(undefined);
  channelsIndicatorSubject = new BehaviorSubject<boolean>(undefined);
  hoursIndicatorSubject = new BehaviorSubject<boolean>(undefined);
  posIndicatorSubject = new BehaviorSubject<boolean>(undefined);
  paymentIndicatorSubject = new BehaviorSubject<boolean>(undefined);
  prepTimeIndicatorSubject = new BehaviorSubject<boolean>(undefined);
  taxRateIndicatorSubject = new BehaviorSubject<boolean>(undefined);
  menusIndicatorSubject = new BehaviorSubject<boolean>(undefined);
  updatedMarkupOverridesSubject = new BehaviorSubject<any>(undefined);
  updatedAvailabilitiesSubject = new BehaviorSubject<OutOfStockUpdateInput[]>([]);
  bulkSelectedStoresSubject = new BehaviorSubject<StoreView[]>(undefined);
  allStoresSelectedSubject = new BehaviorSubject<boolean>(false);
  storeGridUpdateDelayMsgSubject = new BehaviorSubject<boolean>(undefined);
  activeTabLabelBehaviorSubject = new BehaviorSubject(undefined);
  storeFilterValuesBehaviorSubject = new BehaviorSubject(undefined);

  constructor(
    private router: Router,
    private storeConfigStatusService: StoreConfigStatusService,
    private storeService: StoreService,
    private taxService: TaxService,
  ) {}

  clearIndicatorSubjects() {
    this.channelsIndicatorSubject.next(undefined);
    this.hoursIndicatorSubject.next(undefined);
    this.posIndicatorSubject.next(undefined);
    this.paymentIndicatorSubject.next(undefined);
    this.prepTimeIndicatorSubject.next(undefined);
    this.taxRateIndicatorSubject.next(undefined);
    this.menusIndicatorSubject.next(undefined);
  }

  setIndicatorSubjects(store: Store) {
    const status = this.storeConfigStatusService.getConfigStatus(store);
    this.channelsIndicatorSubject.next(status.hasAggregator || false);
    this.hoursIndicatorSubject.next(status.hasStoreHours);
    this.posIndicatorSubject.next(status.hasActivationId);
    this.paymentIndicatorSubject.next(status.hasActivationId);
    this.prepTimeIndicatorSubject.next(status.hasPrepTime);
    // Default tax rate is not currently included in an individual store's response
    this.taxService.getAllTaxOverrides(store.id).subscribe({
      next: ({ storeTaxRate }: StoreTaxOverrides) => {
        this.taxRateIndicatorSubject.next(IsNumberService.isNumber(storeTaxRate));
      },
    });
    this.menusIndicatorSubject.next(status.hasMenus);
  }

  private normalizeStore(store: Partial<Store>): Partial<Store> {
    if (!store) {
      return store;
    }

    const normalizedStore: Partial<Store> = { ...store };
    const timeZoneCodeFromTimeZone = normalizedStore.timeZone?.timeZoneCode;

    if (timeZoneCodeFromTimeZone) {
      normalizedStore.timeZoneCode = timeZoneCodeFromTimeZone;
    }

    return normalizedStore;
  }

  update(newValues: Partial<Store>) {
    const currValue = this.storeSubject.getValue() || {};
    const mergedStore = Object.assign({}, currValue, newValues);
    this.storeSubject.next(this.normalizeStore(mergedStore) as Store);
    this.setIndicatorSubjects(this.storeSubject.value);
  }

  resolve(route: ActivatedRouteSnapshot): Observable<Store> {
    const storeId = route.paramMap.get('id');

    return this.storeService.getOne(storeId).pipe(
      take(1),
      catchError((e) => {
        this.storeSubject.next(undefined);
        this.errorSubject.next(e);
        return EMPTY;
      }),
      tap((store) => {
        this.errorSubject.next(undefined);
        if (store) {
          const normalizedStore = this.normalizeStore(store) as Store;

          this.setIndicatorSubjects(normalizedStore);
          this.storeSubject.next(normalizedStore);

          if (normalizedStore.posIntegrationConfigurations?.posProvider === 'POS_SPI') {
            // Trigger the adapter download URL in the background
            this.storeService
              .getAdapterDownLoadUrl(storeId)
              .pipe(
                catchError(() => of({})), // Handle errors gracefully
              )
              .subscribe((response: Partial<Store>) => {
                if (response && response.adapterDownloadUrl) {
                  const updatedStore = {
                    ...normalizedStore,
                    adapterDownloadUrl: response.adapterDownloadUrl,
                  };
                  this.storeSubject.next(updatedStore); // Update the store once URL is available
                }
              });
          }
        } else {
          // Store not found, navigate away
          this.storeSubject.next(undefined);
          this.router.navigate(['/stores']);
        }
      }),
      map((store) => (this.normalizeStore(store) as Store) || (EMPTY as any)),
    );
  }
}
